/**
 * RequestVoidDialog — non-founder operator's path to void an issued invoice.
 *
 * Voids are Founder-only at the domain level (per system-decisions §12).
 * Pre-PR-I, that meant the Void button just didn't show for operators —
 * leaving the operator stuck if they noticed an error after issue. PR-I
 * lands the missing piece: operator clicks "Request void" → an approval
 * lands in the founder's inbox → founder approves → void runs.
 *
 * Mirror shape of VoidInvoiceDialog (mandatory reason, min 3 chars). The
 * difference is what the submit does: this one calls `approvals.request`,
 * not `invoices.void`.
 */
import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import {
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Input,
  Label,
} from "@flint/ui";

export interface RequestVoidDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `INV/2026-27/0001` or `Draft`. Used in the body copy. */
  docNumber: string | null;
  /** invoice id — written to the approval's `entityId`. */
  invoiceId: string;
  pending?: boolean;
  errorMessage?: string;
  /** Fires only when reason passes the min-length client guard. */
  onConfirm: (payload: { reason: string }) => void;
}

const MIN_REASON_LEN = 3;

export function RequestVoidDialog({
  open,
  onOpenChange,
  docNumber,
  invoiceId,
  pending,
  errorMessage,
  onConfirm,
}: RequestVoidDialogProps) {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason("");
      setTouched(false);
    }
  }, [open]);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < MIN_REASON_LEN;
  const showInline = touched && tooShort;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (tooShort) return;
    onConfirm({ reason: trimmed });
  };

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <form onSubmit={handleSubmit} noValidate>
          <DialogHeader>
            <div className="flex items-start gap-3">
              <span
                aria-hidden
                className="inline-grid place-items-center h-9 w-9 rounded-md bg-amber-50 text-amber-700 shrink-0"
              >
                <Send size={16} />
              </span>
              <div className="flex-1">
                <DialogTitle>Request void</DialogTitle>
                <DialogDescription>
                  Voiding {docNumber ?? "this invoice"} requires a Founder decision. We'll file an
                  approval — the Founder gets notified, and the void runs only after they approve.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="flex flex-col gap-1.5 mt-4">
            <Label htmlFor="request-void-reason-input">Reason for the Founder</Label>
            <Input
              id="request-void-reason-input"
              autoFocus
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setTouched(true);
              }}
              maxLength={500}
              placeholder="e.g. Customer cancelled before dispatch."
              aria-invalid={showInline}
            />
            {showInline ? (
              <p className="text-sm text-bad">
                Reason must be at least {MIN_REASON_LEN} characters.
              </p>
            ) : (
              <p className="text-sm text-muted">
                Lands on the Founder's inbox card. Be specific — speeds up the decision.
              </p>
            )}
            {errorMessage ? <p className="text-sm text-bad mt-1">{errorMessage}</p> : null}
            {/* Hidden input — invoiceId is in scope for the parent's onConfirm. */}
            <input type="hidden" value={invoiceId} readOnly />
          </div>

          <div className="flex items-center justify-end gap-2 mt-5">
            <Button
              type="button"
              intent="ghost"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button type="submit" intent="primary" disabled={pending || tooShort}>
              {pending ? "Filing…" : "File request"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}
